/**
 * @module components/Auth/ResetPasswordForm
 * @description Formulario de restablecimiento de contraseña (flujo de recuperación).
 *
 * Se muestra cuando el usuario llega desde el link del email de recuperación
 * (`isRecoveryMode = true` en AuthContext). Permite ingresar y confirmar una
 * nueva contraseña. Al guardar, llama a `AuthContext.updatePassword()` y
 * redirige al dashboard.
 *
 * El formulario valida que la nueva contraseña cumpla los requisitos de seguridad
 * (mínimo 8 caracteres, al menos una mayúscula y un número).
 *
 * @see AuthContext.isRecoveryMode que controla cuándo se muestra este componente
 * @see App.tsx que renderiza este formulario condicionalmente
 */
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, ShieldCheck, BarChart3, FileCheck, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const FEATURES = [
  { icon: ShieldCheck, title: 'Cumplimiento ISO 27001/27002', description: 'Gestión integral de controles y evidencias de seguridad.' },
  { icon: BarChart3,   title: 'Dashboards en tiempo real',   description: 'Métricas de cumplimiento y alertas críticas por nivel jerárquico.' },
  { icon: FileCheck,   title: 'Auditoría completa',           description: 'Trazabilidad total de cada cambio con registro automático.' },
  { icon: Users,       title: 'Gestión de activos y riesgos', description: 'Inventario ISO 27005 con evaluación de amenazas y vulnerabilidades.' },
];

const validatePassword = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < 8)          errors.push('Mínimo 8 caracteres');
  if (!/[A-Z]/.test(password))      errors.push('Al menos una mayúscula');
  if (!/[a-z]/.test(password))      errors.push('Al menos una minúscula');
  if (!/[0-9]/.test(password))      errors.push('Al menos un número');
  if (!/[!@#$%^&*]/.test(password)) errors.push('Al menos un símbolo (!@#$%^&*)');
  return errors;
};

export function ResetPasswordForm() {
  const { changePassword } = useAuth();
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [isLoading, setIsLoading]             = useState(false);
  const [error, setError]                     = useState<string | null>(null);

  const passwordErrors  = newPassword ? validatePassword(newPassword) : [];
  const passwordsMatch  = newPassword && confirmPassword && newPassword === confirmPassword;
  const isValid         = passwordErrors.length === 0 && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) { setError('Corrija los errores antes de continuar'); return; }
    setError(null);
    setIsLoading(true);
    try {
      await changePassword(newPassword);
      // AuthContext limpia isRecoveryMode y el hash → App.tsx renderiza MainLayout
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel: Branding ── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-[#0B1E3D] flex-col justify-between p-12 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        <div className="absolute top-[-120px] left-[-120px] w-[500px] h-[500px] rounded-full bg-blue-600 opacity-10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[-80px] right-[-80px] w-[350px] h-[350px] rounded-full bg-indigo-500 opacity-10 blur-[100px] pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center space-x-4 mb-3">
            <img src="/acsgc-logo.png" alt="ACS GLOBAL CORE" className="h-14 w-auto object-contain" />
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-blue-400 uppercase">ACS GLOBAL CORE</p>
              <p className="text-[11px] text-slate-500 tracking-wide mt-0.5">Gestión de Cumplimiento</p>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-6">
            Plataforma oficial<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
              de cumplimiento normativo
            </span>
          </h1>
          <p className="text-slate-400 text-base leading-relaxed max-w-md mb-10">
            Sistema certificado para la gestión de controles ISO 27001/27002/27005 y
            cumplimiento de la Ley Marco de Ciberseguridad.
          </p>
          <div className="grid grid-cols-1 gap-5">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom badges */}
        <div className="relative z-10 flex items-center space-x-4">
          {['ISO 27001', 'ISO 27002', 'ISO 27005', 'Ley 21.663'].map(label => (
            <span key={label} className="px-2.5 py-1 text-[10px] font-semibold tracking-wide text-blue-300 border border-blue-800 rounded-full bg-blue-900/30">
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Right panel: Form ── */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col justify-center bg-white dark:bg-gray-950 px-8 sm:px-16 lg:px-20 py-12">

        {/* Mobile logo */}
        <div className="flex justify-center mb-10 lg:hidden">
          <img src="/acsgc-logo.png" alt="ACS GLOBAL CORE" className="h-16 w-auto object-contain" />
        </div>

        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            Nueva contraseña
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
            Ingrese y confirme su nueva contraseña para completar el proceso de recuperación.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3.5 flex items-start space-x-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-300 leading-snug">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nueva contraseña */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
              Nueva contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full pl-10 pr-11 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all outline-none"
                placeholder="••••••••"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Validación inline */}
            {newPassword && (
              <ul className="mt-2 space-y-1">
                {[
                  { label: 'Mínimo 8 caracteres',       ok: newPassword.length >= 8 },
                  { label: 'Al menos una mayúscula',     ok: /[A-Z]/.test(newPassword) },
                  { label: 'Al menos una minúscula',     ok: /[a-z]/.test(newPassword) },
                  { label: 'Al menos un número',         ok: /[0-9]/.test(newPassword) },
                  { label: 'Al menos un símbolo (!@#$%^&*)', ok: /[!@#$%^&*]/.test(newPassword) },
                ].map(({ label, ok }) => (
                  <li key={label} className={`flex items-center space-x-1.5 text-xs ${ok ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                    <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? 'opacity-100' : 'opacity-30'}`} />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Confirmar contraseña */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
              Confirmar contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-11 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white dark:focus:bg-gray-800 transition-all outline-none"
                placeholder="••••••••"
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="mt-1.5 text-xs text-red-500">Las contraseñas no coinciden</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !isValid}
            className="w-full mt-2 py-3.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center space-x-2 shadow-sm"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Actualizando…</span>
              </>
            ) : (
              <span>Establecer nueva contraseña</span>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-12 text-center space-y-1">
          <p className="text-[11px] text-gray-400 dark:text-gray-600">
            ACS Global Core &nbsp;·&nbsp; v2.0.0
          </p>
          <p className="text-[11px] text-gray-300 dark:text-gray-700">
            Plataforma certificada ISO 27001 / 27002 / 27005
          </p>
        </div>
      </div>
    </div>
  );
}
